import { ThemeEvents } from '@theme/events';

/**
 * PDP payment options row (blocks/pdp-payment-options.liquid).
 *
 * Recomputes the displayed instalment amounts for the selected variant. These are the providers'
 * real figures (pay-in-4 = price/4, Zip = price/12). Prices come from the block's embedded
 * variant→price (cents) map, keyed by the variant id on the variant:update event. Studio19's
 * weekly rate is handled separately by studio19-price.js.
 */

const formatter = new Intl.NumberFormat('en-AU', { style: 'currency', currency: 'AUD' });

/** @returns {Record<string, number> | null} variant id -> price in cents */
function readPriceMap(root) {
  const el = root.querySelector('[data-pdp-payments-prices]');
  if (!el) return null;
  try {
    return JSON.parse(el.textContent);
  } catch {
    return null;
  }
}

function updateAmounts(dollars) {
  const payIn4 = formatter.format(dollars / 4);
  const monthly = formatter.format(dollars / 12);
  for (const el of document.querySelectorAll('.pdp-payments [data-pay-in-4]')) {
    // Afterpay/PayPal pay-in-4 only applies up to a per-provider order-value cap (data-limit-max).
    // Above it, show the eligible range (data-limit-range) instead of a misleading instalment.
    const max = Number.parseFloat(el.dataset.limitMax ?? '');
    const range = el.dataset.limitRange;
    el.textContent = range && Number.isFinite(max) && dollars > max ? range : payIn4;
  }
  for (const el of document.querySelectorAll('.pdp-payments [data-zip-monthly]')) {
    el.textContent = `${monthly}/mth`;
  }
  // Keep the hidden Zip widget's price in sync so its terms modal quotes the right amount.
  for (const el of document.querySelectorAll('.pdp-payments [data-zip-widget]')) {
    el.setAttribute('data-zm-price', dollars.toFixed(2));
  }
  // PayPal Pay Later message re-quotes when its amount attribute changes.
  for (const el of document.querySelectorAll('.pdp-payments [data-pp-message]')) {
    el.setAttribute('data-pp-amount', dollars.toFixed(2));
  }
}

/**
 * Open a provider's official modal by clicking its (hidden) vendor widget. Neither Zip nor PayPal
 * documents a programmatic "open" API, so we dispatch a click on the rendered widget/message (and
 * its children). The "More info" trigger is a <button> (no href), so if the widget hasn't rendered
 * we open the provider's info page in a new tab via the trigger's data-fallback-url.
 */
function wireModalTrigger(triggerSelector, widgetSelector) {
  const trigger = document.querySelector(`.pdp-payments ${triggerSelector}`);
  const widget = document.querySelector(`.pdp-payments ${widgetSelector}`);
  if (!trigger) return;

  trigger.addEventListener('click', () => {
    if (widget && widget.childElementCount > 0) {
      const clickable = widget.querySelector('a, button, [role="button"], slot, img') || widget;
      clickable.click();
      return;
    }
    // Widget hasn't rendered — fall back to the provider's info page.
    const url = trigger.dataset.fallbackUrl;
    if (url) window.open(url, '_blank', 'noopener');
  });
}

function init() {
  wireModalTrigger('[data-zip-more-info]', '[data-zip-widget]');
  wireModalTrigger('[data-paypal-more-info]', '[data-pp-message]');

  const row = document.querySelector('.pdp-payments');
  const priceMap = row && readPriceMap(row);
  if (!priceMap) return;

  document.addEventListener(ThemeEvents.variantUpdate, (event) => {
    const variantId = event.detail?.resource?.id;
    const priceCents = variantId != null ? priceMap[String(variantId)] : null;
    if (priceCents != null) updateAmounts(Number(priceCents) / 100);
  });
}

if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', init, { once: true });
} else {
  init();
}
